import axios from "../utils/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

// Helper function to convert tradingHours object to array format expected by backend
const convertTradingHoursToArray = (tradingHours) => {
  if (!tradingHours) return undefined;

  // If already an array, return as is
  if (Array.isArray(tradingHours)) return tradingHours;

  // Convert object format to array format
  const daysMap = {
    monday: "MONDAY",
    tuesday: "TUESDAY",
    wednesday: "WEDNESDAY",
    thursday: "THURSDAY",
    friday: "FRIDAY",
    saturday: "SATURDAY",
    sunday: "SUNDAY"
  };

  return Object.entries(tradingHours)
    .filter(([_, hours]) => hours?.open || hours?.close)
    .map(([day, hours]) => ({
      day: daysMap[day.toLowerCase()] || day.toUpperCase(),
      openTime: hours?.open || "",
      closeTime: hours?.close || ""
    }));
};

export const addFranchise = createAsyncThunk(
  "location",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const merchantId = selectedMerchant?._id || payload?.merchant;
      const url = merchantId ? `location?merchantId=${merchantId}` : "location";

      // If `locations` key exists and is a non-empty array, send only that
      const dataToSend = Array.isArray(payload.locations)
        ? {
          locations: payload.locations.map((loc) => ({
            name: loc?.name,
            email: loc?.email || "",
            phone: String(loc?.phone || "").replace(/\D/g, ""),
            googlePlaceId: typeof loc?.googlePlaceId === 'string'
              ? loc.googlePlaceId
              : (loc?.googlePlaceId?.address || ""),
            tradingHours: convertTradingHoursToArray(loc?.workingHours || loc?.tradingHours),
          })),
        }
        : {
          merchant: payload?.merchant,
          name: payload.name,
          email: payload.email,
          phone: String(payload.phone || "").replace(/\D/g, ""),
          googlePlaceId: typeof payload?.googlePlaceId === 'string'
            ? payload.googlePlaceId
            : (payload?.googlePlaceId?.address || ""),
          tradingHours: convertTradingHoursToArray(payload.tradingHours),
        };

      const response = await axios.post(url, dataToSend);
      return response?.data;
    } catch (error) {
      const raw = error?.response?.data || error?.message;
      const message = typeof raw === "string" ? raw : raw?.message;

      // Check for different types of duplicates
      const isDupPhone = (typeof message === "string" && /E11000/i.test(message) && /phone/i.test(message)) ||
        (typeof message === "string" && /This number already exist/i.test(message));

      const isDupEmail = typeof message === "string" && /E11000/i.test(message) && /email/i.test(message);

      const isDupName = typeof message === "string" && /E11000/i.test(message) && /name/i.test(message);

      let finalMessage;
      if (isDupPhone) {
        finalMessage = "A store location with this phone number is already registered. Please use a different number.";
      } else if (isDupEmail) {
        finalMessage = "A store location with this email address is already registered. Please use a different email.";
      } else if (isDupName) {
        finalMessage = "A store location with this name already exists. Please use a different name.";
      } else {
        finalMessage = message || "Failed to create location";
      }

      return thunkAPI.rejectWithValue({
        success: false,
        message: finalMessage,
      });
    }
  }
);

export const getFranchises = createAsyncThunk(
  "allLocations",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const query = new URLSearchParams();

      if (payload?.merchant) query.append("merchant", payload.merchant);
      if (payload?.page) query.append("page", payload.page);
      if (payload?.search) query.append("search", payload.search);
      if (payload?.duration) query.append("duration", payload.duration);

      if (payload?.limit) query.append("limit", payload.limit);
      else if (!payload?.duration) query.append("limit", 100);

      if (selectedMerchant?._id) query.append("merchantId", selectedMerchant._id);

      const url = `location/merchant?${query.toString()}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const toggleFranchiseStatus = createAsyncThunk(
  "location/status",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch(`location/status/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const editFranchise = createAsyncThunk(
  "location/update",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const merchantId = selectedMerchant?._id || payload?.merchant;
      const url = merchantId
        ? `location/${payload?.id}?merchantId=${merchantId}`
        : `location/${payload?.id}`;
      const response = await axios.patch(url, payload);
      return response?.data;
    } catch (error) {
      const raw = error?.response?.data || error?.message;
      const message = typeof raw === "string" ? raw : raw?.message;

      // Check for different types of duplicates
      const isDupPhone = (typeof message === "string" && /E11000/i.test(message) && /phone/i.test(message)) ||
        (typeof message === "string" && /This number already exist/i.test(message));

      const isDupEmail = typeof message === "string" && /E11000/i.test(message) && /email/i.test(message);

      const isDupName = typeof message === "string" && /E11000/i.test(message) && /name/i.test(message);

      let finalMessage;
      if (isDupPhone) {
        finalMessage = "A store location with this phone number is already registered. Please use a different number.";
      } else if (isDupEmail) {
        finalMessage = "A store location with this email address is already registered. Please use a different email.";
      } else if (isDupName) {
        finalMessage = "A store location with this name already exists. Please use a different name.";
      } else {
        finalMessage = message || "Failed to update location";
      }

      return thunkAPI.rejectWithValue({
        success: false,
        message: finalMessage,
      });
    }
  }
);

export const deleteFranchise = createAsyncThunk(
  "location/delete",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const merchantId = selectedMerchant?._id || payload?.merchant;
      const url = merchantId
        ? `location/${payload?.id}?merchantId=${merchantId}`
        : `location/${payload?.id}`;
      const response = await axios.delete(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getLocationById = createAsyncThunk(
  "location/id",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    const params = selectedMerchant ? { merchantId: selectedMerchant._id } : {};
    try {
      const response = await axios.get(`location/${payload?.id}`, { params });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);